import React, { useMemo } from 'react';
import type { BandwidthCrossFilterSlice } from './bandwidthCrossFilter';
import type { BandwidthTeamResourceAllocationRow } from './bandwidthDashboardTypes';
import {
  BandwidthAllocationPanel,
  useAllocationSort,
  useExpandableRows,
  type AllocationTableRow,
} from './BandwidthAllocationTable';
import { computeAllocationFooter, sortTeamResourceAllocationRows } from './bandwidthAllocation';
import { exportBandwidthTeamResourceAllocationExcel } from './bandwidthDashboardExcelExport';
import { exportBandwidthTeamResourceAllocationPdf } from './bandwidthDashboardPdfExport';

export interface BandwidthTeamResourceTableProps {
  rows: BandwidthTeamResourceAllocationRow[];
  loading?: boolean;
  subtitle?: string;
  exportDateFrom: string;
  exportDateTo: string;
  crossFilter: BandwidthCrossFilterSlice;
  onTeamClick: (teamId: string) => void;
  assigneeUserIds: string[] | null;
  onResourceClick: (userId: string) => void;
}

const BandwidthTeamResourceTable: React.FC<BandwidthTeamResourceTableProps> = ({
  rows,
  loading = false,
  subtitle,
  exportDateFrom,
  exportDateTo,
  crossFilter,
  onTeamClick,
  assigneeUserIds,
  onResourceClick,
}) => {
  const { sortKey, sortDir, toggleSort } = useAllocationSort('totalHours');

  const sortedRows = useMemo(
    () => sortTeamResourceAllocationRows(rows, sortKey, sortDir),
    [rows, sortKey, sortDir]
  );

  const teamIds = useMemo(
    () => sortedRows.filter((r) => (r.resources?.length ?? 0) > 0).map((r) => r.id),
    [sortedRows]
  );
  const teamsToExpandForAssignee = useMemo(() => {
    if (!assigneeUserIds?.length) return [];
    return sortedRows
      .filter((t) => t.resources?.some((u) => assigneeUserIds.includes(u.id)))
      .map((t) => t.id);
  }, [assigneeUserIds, sortedRows]);

  const { expanded, toggle, allExpanded, toggleExpandAll } = useExpandableRows(
    teamIds,
    teamsToExpandForAssignee
  );

  const flatRows = useMemo(() => {
    const out: AllocationTableRow[] = [];
    for (const team of sortedRows) {
      const hasResources = (team.resources?.length ?? 0) > 0;
      const isOpen = expanded[team.id] === true;
      const teamSelected = crossFilter?.kind === 'group' && crossFilter.id === team.id;
      out.push({
        id: team.id,
        label: team.label,
        depth: 0,
        expandable: hasResources,
        rowKind: 'team',
        teamId: team.id,
        selected: teamSelected,
        metrics: team,
      });
      if (hasResources && isOpen) {
        for (const resource of team.resources ?? []) {
          const userSelected = assigneeUserIds != null && assigneeUserIds.includes(resource.id);
          out.push({
            id: `${team.id}:${resource.id}`,
            label: resource.label,
            depth: 1,
            rowKind: 'resource',
            userId: resource.id,
            selected: userSelected,
            metrics: resource,
          });
        }
      }
    }
    return out;
  }, [sortedRows, expanded, crossFilter, assigneeUserIds]);

  const footer = useMemo(
    () =>
      computeAllocationFooter(
        sortedRows,
        sortedRows.reduce((sum, team) => sum + team.totalHours, 0)
      ),
    [sortedRows]
  );

  return (
    <BandwidthAllocationPanel
      title="By Team and Resource View"
      subtitle={subtitle}
      labelColumnTitle="Team / Resource"
      rows={flatRows}
      footer={footer}
      loading={loading}
      expanded={expanded}
      onToggleExpand={toggle}
      allExpanded={allExpanded}
      onToggleExpandAll={toggleExpandAll}
      expandAllDisabled={loading || teamIds.length === 0}
      sortKey={sortKey}
      sortDir={sortDir}
      onToggleSort={toggleSort}
      onTeamClick={onTeamClick}
      onResourceClick={onResourceClick}
      exportAriaLabel="Export team and resource allocation"
      buildExcel={() => exportBandwidthTeamResourceAllocationExcel(sortedRows, exportDateFrom, exportDateTo)}
      buildPdf={() => exportBandwidthTeamResourceAllocationPdf(sortedRows, exportDateFrom, exportDateTo)}
    />
  );
};

export default BandwidthTeamResourceTable;
